import type { VisualPayload } from "../../types";
import { TYPE_TEXT_COLORS } from "../../utils/typeColors";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }

function StatBar({ label, value, max = 300 }: { label: string; value: number; max?: number }) {
  const pct = Math.min(100, (value / max) * 100);
  const color = value >= 200 ? "bg-green-500" : value >= 130 ? "bg-scout-yellow" : "bg-scout-blue";
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-white/40 uppercase tracking-wide">{label}</span>
        <span className="text-sm font-bold text-white">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function PokemonCard({ payload }: Props) {
  const header = payload.sections.find((s) => s.section === "header");
  const stats  = payload.sections.find((s) => s.section === "base_stats");
  const type1 = (header?.type1 as string | undefined) ?? "normal";
  const typeColor = TYPE_TEXT_COLORS[type1.toLowerCase()] ?? "text-gray-300";

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {header && (
        <div className="flex items-start gap-3 mb-4">
          {/* Hero sprite — sized "lg" (56-64px) since this card's whole
              point is one Pokémon.  Stays left-aligned with the name +
              type block; badges still float to the far right.  When
              sprites are disabled, the sprite component returns null
              and the rest of the layout reflows naturally — the gap-3
              just collapses. */}
          <PokemonSprite
            pokedexId={header.pokedex_id as number | undefined}
            alt={header.pokemon_name as string}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white leading-tight">{header.pokemon_name as string}</p>
            <p className="text-xs text-white/40 mt-0.5">
              <span className="text-white/25">#</span>{header.pokedex_id as number}
            </p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className={`text-xs font-medium ${typeColor}`}>{header.type1 as string}</span>
              {typeof header.type2 === "string" && header.type2 && (
                <>
                  <span className="text-white/20">·</span>
                  <span className={`text-xs font-medium ${TYPE_TEXT_COLORS[header.type2.toLowerCase()] ?? "text-gray-300"}`}>
                    {header.type2}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            {!!(header.is_shiny_available) && (
              <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-[11px] font-medium">✨ Shiny</span>
            )}
            {!!(header.is_shadow_available) && (
              <span className="px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[11px] font-medium">Shadow</span>
            )}
            {!!(header.is_mega) && (
              <span className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-medium">Mega</span>
            )}
          </div>
        </div>
      )}

      {stats && (
        <div className="space-y-2.5">
          <StatBar label="ATK" value={stats.attack as number} />
          <StatBar label="DEF" value={stats.defense as number} />
          <StatBar label="STA" value={stats.stamina as number} />
        </div>
      )}

      {stats && (
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-white/[0.06]">
          <div className="text-center flex-1">
            <p className="text-[11px] text-white/30 uppercase tracking-wide">L40 CP</p>
            <p className="text-sm font-bold text-white">{(stats.max_cp_l40 as number).toLocaleString()}</p>
          </div>
          <div className="w-px h-6 bg-white/[0.06]" />
          <div className="text-center flex-1">
            <p className="text-[11px] text-white/30 uppercase tracking-wide">L50 CP</p>
            <p className="text-sm font-bold text-white">{(stats.max_cp_l50 as number).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* CP arithmetic (plan phase 2): hundo CPs, CP at a level, level for a CP */}
      {(() => {
        const cp = payload.sections.find((s) => s.section === "cp_math") as Record<string, unknown> | undefined;
        if (!cp || !Array.isArray(cp.rows) || cp.rows.length === 0) return null;
        const rows = cp.rows as Record<string, unknown>[];
        const ivs = typeof cp.ivs === "string" && cp.ivs ? cp.ivs : null;
        return (
          <div className="mt-4 pt-3 border-t border-white/[0.06]">
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">{ivs ? `CP at ${ivs}` : "CP by level"}</p>
            <div className="space-y-1">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Level {String(r.level)}{typeof r.ivs === "string" ? ` · ${r.ivs}` : ""}</span>
                  <span className="font-mono text-white/90">
                    {typeof r.cp === "number" ? r.cp.toLocaleString() : `${(r.cp_min as number).toLocaleString()} – ${(r.cp_max as number).toLocaleString()}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Pokédex facts (plan phase 4): generation, rarity, buddy distance */}
      {(() => {
        const dex = payload.sections.find((s) => s.section === "pokedex") as Record<string, unknown> | undefined;
        if (!dex) return null;
        const bits: string[] = [];
        if (typeof dex.generation === "number") bits.push(`Generation ${dex.generation}`);
        if (typeof dex.rarity === "string" && dex.rarity !== "Standard") bits.push(dex.rarity);
        if (typeof dex.buddy_distance_km === "number") bits.push(`Buddy: ${dex.buddy_distance_km} km per Candy`);
        if (!bits.length) return null;
        return <p className="mt-3 text-[11px] text-white/45">{bits.join(" · ")}</p>;
      })()}

      {/* Mega Energy (plan phase 4): each Mega form's first-time and later cost */}
      {(() => {
        const mega = payload.sections.find((s) => s.section === "mega_energy") as Record<string, unknown> | undefined;
        if (!mega || !Array.isArray(mega.megas) || mega.megas.length === 0) return null;
        const megas = mega.megas as { mega_name: string; first_time: number; subsequent: number }[];
        return (
          <div className="mt-4 pt-3 border-t border-white/[0.06]">
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1.5">Mega Energy</p>
            <div className="space-y-1">
              {megas.map((m) => (
                <div key={m.mega_name} className="flex items-center justify-between text-sm">
                  <span className="text-white/60">{m.mega_name}</span>
                  <span className="font-mono text-white/90">{m.first_time} first · {m.subsequent} after</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Weaknesses section */}
      {payload.sections.some((s) => s.section === "weaknesses") && (
        <div className="mt-6">
          <div className="font-semibold text-sm mb-2 text-white/80">Type Weaknesses & Resistances</div>
          {(() => {
            const weaknesses = payload.sections.find((s) => s.section === "weaknesses") as any;
            const BUCKET_LABELS: Record<string, string> = {
              double_weak: "Double Weak (x2.56+)",
              weak: "Weak (x1.25–2.56)",
              resist: "Resist (x0.4–0.8)",
              double_resist: "Double Resist (≤0.4)",
            };
            return Object.entries(BUCKET_LABELS).map(([bucket, label]) => {
              const types = weaknesses[bucket] as string[];
              if (!types || types.length === 0) return null;
              return (
                <div key={bucket} className="mb-2">
                  <div className="text-xs font-medium text-white/60 mb-1">{label}</div>
                  <div className="flex flex-wrap gap-1">
                    {types.map((type) => (
                      <span key={type} className={`inline-block px-2 py-1 rounded border text-xs font-semibold ${TYPE_TEXT_COLORS[type.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </CardShell>
  );
}
